/**
 * SignalCard — supporting dashboard metric with optional inline visualization.
 *
 * Use for secondary KPIs around a headline ScoreCard. Each card shows a title,
 * a value, optional subtitle, and one of several visuals chosen via `visual`:
 *
 *  - `plain`     — number only (default)
 *  - `ring`      — small radial gauge for progress toward a target
 *  - `bar`       — horizontal progress bar (continuous 0–100%)
 *  - `segments`  — discrete N-of-M indicator; use when total is small (≤16)
 *  - `bars`      — grouped mini bar chart from a breakdown[]
 *  - `sparkline` — trailing-period line chart from a trend[]
 *
 */
import { ReactNode } from 'react';
import { Info } from 'lucide-react';
import {
    RadialBarChart,
    RadialBar,
    PolarAngleAxis,
    LineChart,
    Line,
    YAxis,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
} from 'recharts';
import { Card, CardContent } from '../ui/card';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../ui/tooltip';
import { getProgressColor } from './colors';

// Default color for data-only visuals (bars, sparkline) when the caller
// doesn't pass `color`. Tailwind blue-400.
const DEFAULT_DATA_COLOR = '#60a5fa';

export interface BreakdownEntry {
    label: string;
    value: number;
}

export interface TrendLabels {
    start: string;
    end: string;
}

interface SignalCardBaseProps {
    title: string;
    icon?: ReactNode;
    value: string | number;
    subtitle?: string;
    tooltip?: string;
    headerControls?: ReactNode;
    className?: string;
}

export type SignalCardProps = SignalCardBaseProps &
    (
        | { visual?: 'plain' }
        | { visual: 'ring'; progress: number; ringCenterLabel?: string }
        | { visual: 'bar'; progress: number }
        | { visual: 'segments'; filled: number; total: number }
        | {
              visual: 'bars';
              breakdown: BreakdownEntry[];
              color?: string;
              breakdownValueFormatter?: (value: number) => string;
          }
        | {
              visual: 'sparkline';
              trend: number[];
              trendFormatter?: (value: number) => string;
              trendLabels?: TrendLabels;
              color?: string;
          }
    );

function MiniRing({
    progress,
    centerLabel,
}: {
    progress: number;
    centerLabel?: string;
}) {
    const clamped = Math.min(Math.max(progress, 0), 100);
    const color = getProgressColor(clamped);
    const size = 56;
    const center = size / 2;
    const innerR = 20;
    const outerR = 26;

    // Tick marks at 25%, 50%, 75% around the ring. Ring starts at top (12 o'clock)
    // and goes clockwise, so 0% = -π/2 radians.
    const tickPositions = [0.25, 0.5, 0.75].map((p) => {
        const angle = 2 * Math.PI * p - Math.PI / 2;
        return {
            x1: center + innerR * Math.cos(angle),
            y1: center + innerR * Math.sin(angle),
            x2: center + outerR * Math.cos(angle),
            y2: center + outerR * Math.sin(angle),
        };
    });

    return (
        <div
            className="relative shrink-0"
            style={{ height: size, width: size }}
        >
            <RadialBarChart
                width={size}
                height={size}
                cx={center}
                cy={center}
                innerRadius={innerR}
                outerRadius={outerR}
                barSize={6}
                data={[{ value: clamped }]}
                startAngle={90}
                endAngle={-270}
            >
                <PolarAngleAxis
                    type="number"
                    domain={[0, 100]}
                    angleAxisId={0}
                    tick={false}
                />
                <RadialBar
                    dataKey="value"
                    cornerRadius={3}
                    fill={color}
                    background={{ fill: 'rgba(255,255,255,0.06)' }}
                />
            </RadialBarChart>
            <svg
                className="pointer-events-none absolute inset-0"
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
            >
                {tickPositions.map((pos, i) => (
                    <line
                        key={i}
                        x1={pos.x1}
                        y1={pos.y1}
                        x2={pos.x2}
                        y2={pos.y2}
                        stroke="rgba(0,0,0,0.55)"
                        strokeWidth={1.25}
                    />
                ))}
            </svg>
            {centerLabel && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <span className="text-[10px] font-medium tabular-nums text-muted-foreground">
                        {centerLabel}
                    </span>
                </div>
            )}
        </div>
    );
}

function BarsBreakdown({
    breakdown,
    color = DEFAULT_DATA_COLOR,
    valueFormatter,
}: {
    breakdown: BreakdownEntry[];
    color?: string;
    valueFormatter?: (value: number) => string;
}) {
    const max = Math.max(...breakdown.map((b) => b.value), 1);
    return (
        <div className="flex shrink-0 items-end gap-3">
            {breakdown.map((entry) => {
                const heightPct = (entry.value / max) * 100;
                return (
                    <div
                        key={entry.label}
                        className="flex flex-col items-center gap-1"
                    >
                        <span className="text-[10px] font-semibold tabular-nums text-foreground">
                            {valueFormatter
                                ? valueFormatter(entry.value)
                                : entry.value}
                        </span>
                        <div className="flex h-8 w-5 items-end overflow-hidden rounded-sm bg-white/[0.06]">
                            <div
                                className="w-full rounded-sm transition-all"
                                style={{
                                    height: `${Math.max(heightPct, entry.value > 0 ? 10 : 0)}%`,
                                    backgroundColor: color,
                                }}
                            />
                        </div>
                        <span className="text-[10px] font-medium tracking-wide text-muted-foreground">
                            {entry.label}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

const DEFAULT_TREND_LABELS: TrendLabels = { start: '14d', end: 'today' };

function Sparkline({
    trend,
    formatter,
    labels = DEFAULT_TREND_LABELS,
    color: colorProp = DEFAULT_DATA_COLOR,
}: {
    trend: number[];
    formatter?: (value: number) => string;
    labels?: TrendLabels;
    color?: string;
}) {
    const data = trend.map((value, index) => ({ index, value }));
    const hasActivity = trend.some((v) => v > 0);

    const color = hasActivity ? colorProp : '#ffffff33';
    return (
        <div className="flex shrink-0 flex-col items-end gap-1">
            <div className="h-[44px] w-[150px]">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={data}
                        margin={{ top: 4, right: 4, bottom: 2, left: 4 }}
                    >
                        <YAxis hide domain={[0, 'dataMax + 1']} />
                        <ReferenceLine
                            y={0}
                            stroke="rgba(255,255,255,0.12)"
                            strokeWidth={1}
                        />
                        <RechartsTooltip
                            cursor={false}
                            wrapperStyle={{ outline: 'none' }}
                            contentStyle={{
                                backgroundColor: 'rgba(15,15,15,0.95)',
                                border: '1px solid rgba(255,255,255,0.1)',
                                borderRadius: '6px',
                                padding: '3px 8px',
                                fontSize: '11px',
                            }}
                            labelFormatter={() => ''}
                            formatter={(value) => [
                                formatter ? formatter(Number(value)) : String(value),
                                '',
                            ]}
                            separator=""
                        />
                        <Line
                            type="monotone"
                            dataKey="value"
                            stroke={color}
                            strokeWidth={1.75}
                            dot={{ r: 1.75, fill: color, strokeWidth: 0 }}
                            activeDot={{ r: 3.5, fill: color, strokeWidth: 0 }}
                            isAnimationActive={false}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
            <div className="flex w-[150px] justify-between px-1 text-[9px] text-muted-foreground/60">
                <span>{labels.start}</span>
                <span>{labels.end}</span>
            </div>
        </div>
    );
}

function ProgressBar({ progress }: { progress: number }) {
    const clamped = Math.min(Math.max(progress, 0), 100);
    const color = getProgressColor(clamped);
    return (
        <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-white/[0.06]">
            <div
                className="h-full rounded-full transition-all"
                style={{ width: `${clamped}%`, backgroundColor: color }}
            />
        </div>
    );
}

function SegmentedBar({ filled, total }: { filled: number; total: number }) {
    const clampedFilled = Math.min(Math.max(filled, 0), total);
    const pct = total > 0 ? (clampedFilled / total) * 100 : 0;
    const color = getProgressColor(pct);
    return (
        <div className="mt-3 flex w-full gap-1">
            {Array.from({ length: total }).map((_, i) => (
                <div
                    key={i}
                    className="h-1.5 flex-1 rounded-full transition-all"
                    style={{
                        backgroundColor:
                            i < clampedFilled
                                ? color
                                : 'rgba(255,255,255,0.06)',
                    }}
                />
            ))}
        </div>
    );
}

export default function SignalCard(props: SignalCardProps) {
    const {
        title,
        icon,
        value,
        subtitle,
        tooltip,
        headerControls,
        className,
    } = props;
    return (
        <Card className={`border-subtle ${className ?? ''}`}>
            <CardContent className="relative flex flex-col justify-between p-4">
                {headerControls && (
                    <div className="absolute top-3 right-4 z-10">
                        {headerControls}
                    </div>
                )}
                {tooltip && (
                    <TooltipProvider delayDuration={200}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Info
                                    className={`absolute top-4 h-3.5 w-3.5 cursor-help text-muted-foreground/40 hover:text-muted-foreground ${
                                        headerControls
                                            ? 'right-20'
                                            : 'right-4'
                                    }`}
                                />
                            </TooltipTrigger>
                            <TooltipContent
                                side="top"
                                className="max-w-[220px] text-xs"
                            >
                                {tooltip}
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                )}
                <div className="flex items-center justify-between">
                    <div className={`flex items-center gap-2 text-xs font-medium tracking-wide text-muted-foreground ${headerControls ? 'pr-24' : tooltip ? 'pr-8' : 'pr-5'}`}>
                        {icon && (
                            <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-white/[0.08] text-foreground/70">
                                {icon}
                            </div>
                        )}
                        {title}
                    </div>
                </div>

                <div className="mt-3 flex items-end justify-between gap-4">
                    <div className="min-w-0">
                        <span className="text-2xl font-semibold tabular-nums text-foreground">
                            {value}
                        </span>
                        {subtitle && (
                            <p className="mt-1 text-xs text-muted-foreground">
                                {subtitle}
                            </p>
                        )}
                    </div>
                    {props.visual === 'ring' && (
                        <MiniRing
                            progress={props.progress}
                            centerLabel={props.ringCenterLabel}
                        />
                    )}
                    {props.visual === 'bars' && (
                        <BarsBreakdown
                            breakdown={props.breakdown}
                            color={props.color}
                            valueFormatter={props.breakdownValueFormatter}
                        />
                    )}
                    {props.visual === 'sparkline' && (
                        <Sparkline
                            trend={props.trend}
                            formatter={props.trendFormatter}
                            labels={props.trendLabels}
                            color={props.color}
                        />
                    )}
                </div>

                {props.visual === 'segments' && (
                    <SegmentedBar
                        filled={props.filled}
                        total={props.total}
                    />
                )}
                {props.visual === 'bar' && (
                    <ProgressBar progress={props.progress} />
                )}
            </CardContent>
        </Card>
    );
}
